// Helper function to split text into sentences
const splitIntoSentences = text => {
  // Split by sentence endings while preserving the delimiter
  return text.match(/[^.!?]+[.!?]+/g) || [text]
}

// Helper function to distribute sentences equally
const distributeSentencesEqually = (sentences, numberOfParts) => {
  const sentencesPerPart = Math.ceil(sentences.length / numberOfParts)
  const parts = []

  for (let i = 0; i < numberOfParts; i++) {
    const start = i * sentencesPerPart
    const end = start + sentencesPerPart
    const partSentences = sentences.slice(start, end)
    parts.push(partSentences.join(' ').trim())
  }

  return parts
}

// Modified getContent to track page indices for duplicate headings
const getContent = data => {
  const selectedPages = data.filter(page => page.pageSelected)
  let contentObj = {}
  let headingPageMap = {} // Track which pages have which headings

  selectedPages.forEach((page, index) => {
    const headingElement = page.data.find(
      element => element.type === 'TEXT' && element.display === 'HEADING'
    )
    const textElement = page.data.find(
      element => element.type === 'TEXT' && element.display !== 'HEADING'
    )

    if (headingElement && textElement) {
      const headingText = cleanTextContent(headingElement.params.content)
      const textContent = cleanTextContent(textElement.params.content)

      if (headingText && textContent) {
        // Track page indices for this heading
        if (!headingPageMap[headingText]) {
          headingPageMap[headingText] = []
          contentObj[headingText] = ''
        }

        headingPageMap[headingText].push(page.id) // or use index if needed

        // Concatenate content with separator for same headings
        if (contentObj[headingText]) {
          contentObj[headingText] += ' ' + textContent
        } else {
          contentObj[headingText] = textContent
        }
      }
    }
  })

  return { contentObj, headingPageMap }
}

// Modified updateContentWithRevisedData
const updateContentWithRevisedData = (data, revisedContent, headingPageMap) => {
  const updatedData = JSON.parse(JSON.stringify(data))
  const selectedPages = updatedData.filter(page => page.pageSelected)

  // Group pages by heading
  const pagesByHeading = {}

  selectedPages.forEach(page => {
    const headingElement = page.data.find(
      element => element.type === 'TEXT' && element.display === 'HEADING'
    )

    if (headingElement) {
      const headingText = cleanTextContent(headingElement.params.content)

      if (!pagesByHeading[headingText]) {
        pagesByHeading[headingText] = []
      }

      pagesByHeading[headingText].push(page)
    }
  })

  // Update content for each heading
  Object.keys(pagesByHeading).forEach(headingText => {
    if (revisedContent[headingText]) {
      const pages = pagesByHeading[headingText]
      const newTextContent = revisedContent[headingText]

      if (pages.length === 1) {
        // Single page - just update normally
        const textElement = pages[0].data.find(
          element => element.type === 'TEXT' && element.display !== 'HEADING'
        )

        if (textElement) {
          textElement.params.content = replaceTextInHTML(
            textElement.params.content,
            newTextContent
          )
        }
      } else {
        // Multiple pages with same heading - split content equally
        const sentences = splitIntoSentences(newTextContent)
        const distributedContent = distributeSentencesEqually(
          sentences,
          pages.length
        )

        pages.forEach((page, index) => {
          const textElement = page.data.find(
            element => element.type === 'TEXT' && element.display !== 'HEADING'
          )

          if (textElement && distributedContent[index]) {
            textElement.params.content = replaceTextInHTML(
              textElement.params.content,
              distributedContent[index]
            )
          }
        })
      }
    }
  })

  return updatedData
}

const replaceTextInHTML = (originalHTML, newText) => {
  if (!originalHTML) return `<p>${newText}</p>`

  const parser = new DOMParser()
  const doc = parser.parseFromString(originalHTML, 'text/html')

  const rootElement = doc.body.firstChild || doc.body

  if (!rootElement || rootElement.nodeType === Node.TEXT_NODE) {
    return `<p>${newText}</p>`
  }

  // Clone the entire structure
  const clonedElement = rootElement.cloneNode(true)

  // Find all text-containing elements (not just text nodes)
  const textElements = []
  const walker = document.createTreeWalker(
    clonedElement,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: node => {
        // Accept elements that contain text (like <p>, <span>, etc.)
        return node.childNodes.length > 0 &&
          Array.from(node.childNodes).some(
            child => child.nodeType === Node.TEXT_NODE
          )
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP
      }
    }
  )

  let currentNode
  while ((currentNode = walker.nextNode())) {
    textElements.push(currentNode)
  }

  if (textElements.length > 0) {
    // Replace text in the first text-containing element
    // (assuming single paragraph structure)
    textElements[0].textContent = newText
  } else {
    // Fallback: replace entire content
    clonedElement.textContent = newText
  }

  return clonedElement.outerHTML
}

// Helper function to properly extract and clean text
const cleanTextContent = htmlContent => {
  if (!htmlContent) return ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, 'text/html')

  // Get text content
  let text = doc.body.textContent || ''

  // Normalize whitespace
  text = text.trim().replace(/\s+/g, ' ')

  // Replace smart quotes with standard quotes (optional, but safer)
  text = text
    .replace(/[\u2018\u2019]/g, "'") // Smart single quotes
    .replace(/[\u201C\u201D]/g, '"') // Smart double quotes
    .replace(/\u2013|\u2014/g, '-') // En/em dashes

  return text
}

export {
  cleanTextContent,
  splitIntoSentences,
  distributeSentencesEqually,
  getContent,
  updateContentWithRevisedData,
  replaceTextInHTML
}
